import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { EnvironmentConfiguration } from 'app/environment/services/environment.configuration';
import { Observable } from 'rxjs/Observable';
import { User } from '../../auth/models/User';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { LogService } from '../../shared/log/log.service';
import { AgencyLookupStore } from '../../shared/lookup/agency-lookup/agency-lookup.store';
import { AgencyLookupDetail } from '../../shared/models/agency-lookup-detail';
import { AgencyLookupSummary } from '../../shared/models/agency-lookup-summary';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { Contact } from '../models/contact';
import { DropdownItem } from '../models/dropdown-item';
import { OnyxEvent } from '../models/event';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { CommonDataStore } from '../service/common-data/common-data.store';
import { EventValidationService } from '../service/event-validation.service';
import { PaymentStore } from '../service/payment.store';
import { AgencyLookupConverter } from '../utils/agency-lookup-converter';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'pioneer-agency-form',
  templateUrl: './agency-form.component.html'
})
export class AgencyFormComponent implements OnInit, OnChanges {

  @Input('parentForm')
  public parentForm: FormGroup;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  @Input()
  public isReadOnly = false;

  @Input()
  public inputEmail: string;

  @Output()
  public outputEmail: EventEmitter<string> = new EventEmitter<string>();

  public contactLookupForm: FormGroup;
  public onyxEvent: OnyxEvent;
  public summaryResults;
  public newTravelAgency = false;
  public isAgencySelected = false;
  public states$: Observable<DropdownItem[]>;
  public selectedCountry$: Observable<DropdownItem>;
  public selectedState$: Observable<DropdownItem>;
  public isStateRequired = PioneerValidators.isStateRequired;
  public isPostalCodeRequired = PioneerValidators.isPostalCodeRequired;

  private lookupSelected: boolean;

  public hasPayments = false;

  private user: User;
  private agencyContactEmail: string;

  get agencyStore(): AgencyLookupStore {
    return this.agencyLookupStore;
  }

  get commonDataStore(): CommonDataStore {
    return this._commonDataStore;
  }

  canChangeAgency$: Observable<boolean>;

  @Output()
  selectedAgency = new EventEmitter();

  constructor(private formBuilder: FormBuilder,
              private agencyLookupStore: AgencyLookupStore,
              private logService: LogService,
              private paymentStore: PaymentStore,
              private _commonDataStore: CommonDataStore,
              private authenticationStore: AuthenticationStore,
              private environment: EnvironmentConfiguration,
              private eventValidationService: EventValidationService) {
    this.user = this.authenticationStore.getUserSync();
  }

  ngOnInit() {
    if (!this.onyxEvent) {
      this.onyxEvent = new OnyxEvent();
    }

    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });

    this.contactLookupForm = this.dataToFormGroup(this.onyxEvent.eventAgency);

    this.agencyLookupStore.selected.subscribe((details: AgencyLookupDetail) => {
      this.logService.verbose(`agency details ${JSON.stringify(details)}`);
      const party = AgencyLookupConverter.agencyLookupDetailToParty(details);
      this.agencyLookupStore.clearSearchResults();
      this.dataToFormGroup(party);
    });

    if (this.onyxEvent.eventAgency && this.onyxEvent.eventAgency.eventPartyId) {
      this.isAgencySelected = true;
    }

    if (!this.onyxEvent.eventId) {
      this.hasPayments = false;
    } else {
      this.paymentStore.getLatestFinalPayment()
      .take(1)
      .subscribe(lastFinalPayment => {
        this.hasPayments = Boolean(this.hasPayments || (lastFinalPayment && lastFinalPayment.eventPaymentId));
      });

    this.paymentStore.getPrepayment()
      .take(1)
      .subscribe(prepayment => {
        this.hasPayments = Boolean(this.hasPayments || (prepayment && prepayment.eventPaymentId));
      });
    }

    this.states$ = this.commonDataStore.states;
    this.states$.subscribe((states) => {
      if (states && states.length > 0) {
        const stateFormControl = this.contactLookupForm ? this.contactLookupForm.get('state') : undefined;
        if (stateFormControl) {
          stateFormControl.enable();
        }
      }
    });

    if (this.user.allArcIatas && !(this.onyxEvent.eventAgency && this.onyxEvent.eventAgency.code)) {
      const agencyUser = new AgencyLookupSummary({
        agencyId: this.user.allArcIatas[0],
        name: '',
        address: null,
        manuallyAdded: false,
      });
      this.onSelectedAgency(agencyUser);
    }

    this.canChangeAgency$ = this.eventValidationService.canChangeAgency();

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.emailValidation() && changes.inputEmail && this.contactLookupForm) {
      this.checkDuplicatedEmailError(this.contactLookupForm.get('contact').get('email').value);
    }

    if (!changes[ 'event' ]) {
      return;
    }

    this.logService.debug(`user logged-in inside ngOnChanges: ${this.user}`);
    this.dataToFormGroup(this.onyxEvent.eventAgency);
  }

  inlineSearchTouched(): void {
    // Check for an error if the user has never tried to select an agency
    // A delay is required so that this logic is done after onSelectedProperty()
    setTimeout(() => {
      if (!this.lookupSelected) {
        this.agencyLookupStore.clearSearchResults();
      } else {
        this.lookupSelected = false;
      }
    }, 100);
  }

  updateResults(results): void {
    this.logService.verbose(`results: ${results}`);
    this.summaryResults = results;
  }

  onSelectedAgency(summary: AgencyLookupSummary) {
    this.lookupSelected = true;
    this.isAgencySelected = true;
    this.newTravelAgency = false;
    this.logService.debug(`agencySummary ${JSON.stringify(summary)}`);
    this.agencyLookupStore.getDetail(summary).subscribe();
    this.selectedAgency.emit();
  }

  private dataToFormGroup(party: Party): FormGroup {

    if (!party) {
      party = new Party();
    } else {
      if (!party.contact) {
        party.contact = new Contact();
      }
      party = new Party(party);
    }

    /*
     * ME-17780 Set the user email as agency contact email by default when user has ViewOnlyUserEvents permission
     */
    this.agencyContactEmail = party.contact.email;
    if (this.authenticationStore.hasPermission(['ViewOnlyUserEvents'])) {
      this.agencyContactEmail = this.agencyContactEmail ? this.agencyContactEmail : this.user.email.toLowerCase();
    }

    this.createForm(party, this.newTravelAgency);

    if (!party.countryCode) {
      this.contactLookupForm.get('state').disable();
    }

    return this.contactLookupForm;
  }

  startCreatingAgency() {
    this.newTravelAgency = true;
    this.isAgencySelected = false;
    this.contactLookupForm = this.dataToFormGroup(null);
  }

  onCountryCodeChanged() {
    this.contactLookupForm.get('state').disable();
    this.contactLookupForm.get('state').reset();
    this.commonDataStore.loadStatesFromCountry(this.contactLookupForm.get('countryCode').value).take(1).subscribe();
  }

  private createForm(party: Party, withValidators: boolean) {
    this.commonDataStore.loadStatesFromCountry(party.countryCode).take(1).subscribe();

    this.contactLookupForm = this.formBuilder.group({
      newTravelAgency: [this.newTravelAgency, null],
      code: [party.code, withValidators ? [Validators.maxLength(8), PioneerValidators.isValidAgencyId] : []],
      name: [
        party.name ? party.name.substring(0, 50) : party.name,
        withValidators ? [Validators.required, Validators.maxLength(50), PioneerValidators.isWhitespaceValidated] : []],
      dbaName: [
        party.dbaName ? party.dbaName.substring(0, 50) : party.dbaName,
        withValidators ? [Validators.maxLength(50)] : []],
      countryCode: [party.countryCode, withValidators ? [Validators.required, PioneerValidators.isWhitespaceValidated] : []],
      countryName: ['', null],
      line1: [party.line1, withValidators ? [Validators.required, Validators.maxLength(50), PioneerValidators.isWhitespaceValidated] : []],
      line2: [party.line2, withValidators ? [Validators.maxLength(50)] : []],
      line3: [party.line3, withValidators ? [Validators.maxLength(50)] : []],
      partyType: [PartyType.AGENCY, null],
      city: [party.city, withValidators ? [Validators.required, Validators.maxLength(50), PioneerValidators.isWhitespaceValidated] : []],
      state: [party.state, null],
      stateName: ['', null],
      postalCode: [party.postalCode, withValidators ? [Validators.maxLength(15)] : []],
      agencyId: [party.code, null],
      autoGeneratedCode: [party.autoGeneratedCode, null],
      manuallyAdded: [party.manuallyAdded, null],
      contact: this.formBuilder.group({
        firstName: [party.contact.firstName, withValidators ? [Validators.maxLength(50)] : []],
        lastName: [party.contact.lastName, withValidators ? [Validators.maxLength(50)] : []],
        partyType: [PartyType.AGENCY, null],
        email: [this.agencyContactEmail,
          [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail]],
        phone: [party.contact.phone, withValidators ? [Validators.maxLength(20)] : []],
        fax: [party.contact.fax, withValidators ? [Validators.maxLength(20)] : []]
      }),
      subscriber: new FormControl(),
      eventAgencyStatus: new FormControl(),
      originalCode: new FormControl()
    }, {
      validator: Validators.compose([
        PioneerValidators.postalCodeCountryCrossValidator('postalCode', 'countryCode'),
        PioneerValidators.countryStateCrossValidator('countryCode', 'state')
      ])
    });

    if (party.contact.email) {
      this.outputEmail.emit(party.contact.email);
    }

    if (this.emailValidation()) {

      this.contactLookupForm.get('contact').get('email').valueChanges.subscribe(value => {
        this.outputEmail.emit(value);

        this.checkDuplicatedEmailError(value);
      });

    }

    this.selectedCountry$ = this.commonDataStore.getCountry(party.countryCode);
    // Set countryName form control
    this.selectedCountry$.subscribe(country => {
      this.contactLookupForm.controls['countryName'].setValue(country.value);
    });

    if (party.countryCode) {
      this.selectedState$ = this.commonDataStore.getState(party.countryCode, party.state);
    } else {
      this.selectedState$ = Observable.of(new DropdownItem('', party.state));
    }
    this.parentForm.setControl('eventAgency', this.contactLookupForm);
  }

  private checkDuplicatedEmailError(formEmailValue) {
    if (formEmailValue && this.inputEmail && formEmailValue.toLowerCase() === this.inputEmail.toLowerCase()) {
      this.contactLookupForm.get('contact').get('email').setErrors({'email_contact_property_agency_same': true});
    } else {
      if (this.contactLookupForm && this.contactLookupForm.get('contact').get('email').errors
          && this.contactLookupForm.get('contact').get('email').errors['email_contact_property_agency_same']) {
        this.contactLookupForm.get('contact').get('email').setErrors(null);
      }
    }
  }

  public emailValidation(): boolean {
    return this.environment.emailValidation;
  }

  public uiShowSystemGeneratedBadge(): boolean {
    if (!this.newTravelAgency && this.contactLookupForm.get('autoGeneratedCode').value) {
      return true;
    } else {
      return false;
    }
  }

  public uiShowManualAgencyWarningMessage(): boolean {
    if (this.newTravelAgency ||  this.contactLookupForm.get('manuallyAdded').value) {
      return true;
    } else {
      return false;
    }
  }

  public isAgencyInlineSearchVisible(): boolean {
    return !this.hasPayments &&
           !this.isReadOnly &&
           (!this.user.allArcIatas || (this.user.allArcIatas && this.user.customerInfo && this.user.customerInfo.length >= 2));
  }

  public isCreateNewAgencyEnabled(): boolean {
    return !this.user.allArcIatas;
  }

  public isAgencyInlineSearchEnabled(): boolean {
    if (this.onyxEvent.eventId === null) {
      return true;
    }
    if (this.authenticationStore.isAgencyUser()) {
      return (this.user.allArcIatas && this.user.customerInfo && this.user.customerInfo.length >= 2);
    } else {
      return (!this.onyxEvent.eventAgency || this.onyxEvent.eventAgency.createdEvent !== 'Y'
        || this.onyxEvent.eventProperty.mergedEvent === 'Y');
    }
  }

  private isFieldEditable(): boolean {
    return (this.authenticationStore.isPropertyUser() || this.authenticationStore.isSupportUser())
      && !this.isReadOnly && !this.hasPayments && !(this.onyxEvent.eventAgency && this.onyxEvent.eventAgency.subscriber);
  }

  private isNewAgency(): boolean {
    return this.newTravelAgency || !this.onyxEvent.eventAgency;
  }

  public isEditableOnlyManuallyAdded(): boolean {
    if (this.isNewAgency()) {
      return true;
    }

    if (this.isFieldEditable() && this.onyxEvent.eventAgency && this.onyxEvent.eventAgency.manuallyAdded) {
      return true;
    }

    return false;
  }

  public isEditableNonSubscriber(): boolean {
    if (this.isNewAgency()) {
      return true;
    }

    if (this.isFieldEditable()) {
      return true;
    }

    return false;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/agency-form/agency-form.component.ts